/**
 * T-209: `/article/<slug>` is not a reader-facing route, and five components
 * were linking readers straight at it.
 *
 * A MotoWalla original is published with `posts.url = /article/<slug>`. That
 * string is a MARKER used to flag original content — there is no route for it.
 * Readers reach editorial at `/post/<uuid>`. The SPA returns 200 for any path,
 * so an outbound `<a href={post.url} target="_blank">` on an original opens a
 * blank shell and nothing ever errors. That is why it survived so long.
 *
 * WHY THIS LIVES IN lib/ AND NOT IN A COMPONENT
 *
 *   The rule was already known and already correct — as `isOriginal`, a const
 *   private to PostDetail. That component guarded itself TWICE and the other
 *   five renderers of `post.url` never learned the distinction existed.
 *   Exactly the shape of T-179's `isValidThumbnail` (private to PostCardGrid,
 *   so the sidebar showed grey boxes): a useful rule trapped inside one
 *   component is a bug waiting in every other one. Patching the five call
 *   sites would leave the sixth to be written next week; the local variable
 *   IS the defect and the five wrong files are its symptom.
 *
 * WHAT AN ORIGINAL RENDERS INSTEAD
 *
 *   Nothing. An original is not an outbound link at all — no ExternalLink
 *   icon, no target="_blank". The reader is already at the source; we are the
 *   source. It is deliberately NOT re-pointed at `/post/<id>`: on a card that
 *   already navigates there, a second control to the same place is chrome.
 *   Absent, not re-pointed.
 */

/** The marker prefix written into `posts.url` by the boot article publisher. */
export const ORIGINAL_URL_PREFIX = '/article/';

/**
 * True when this post is MotoWalla's own editorial, whose `url` is an internal
 * marker rather than a fetchable address.
 *
 * Deliberately keyed on the url ALONE, unlike PostDetail's old local which also
 * required `cleanContent`. That extra condition was right for PostDetail (it
 * renders the body, so it needs the body to exist) but wrong as the general
 * rule: an original whose body failed to load is still not an outbound link,
 * and under the old predicate it would have been treated as one — a dead link
 * on exactly the post that was already broken.
 */
export function isOriginalPost(post: { url?: string | null }): boolean {
  return Boolean(post.url?.startsWith(ORIGINAL_URL_PREFIX));
}

/**
 * The href to use for an outbound "read at the source" control, or `null` when
 * there is no such thing because we ARE the source.
 *
 * Callers must render no anchor at all when this returns null. Returning null
 * rather than `'#'` or the post's own route is the point: a null cannot be
 * accidentally rendered into a working-looking link.
 */
export function outboundUrl(post: { url?: string | null }): string | null {
  if (!post.url || isOriginalPost(post)) return null;
  return post.url;
}
